import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import {
  AlertTriangle, Layers, LayoutGrid, List, Pencil, Plus,
  Search, Trash2, Users, XCircle,
} from "lucide-react";
import { formatDate, Modal, ImageField } from "./_shared";


const CATEGORIES = ["Conference", "Workshop", "MAD Week", "Webinar", "Field Trip"];
const EMPTY_EVENT = {
  title: "", description: "", date: "", location: "",
  category: "Conference", image_url: "", capacity: 100, status: "open",
};

export function EventsPanel() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewingRes, setViewingRes] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("detail"); // detail | compact
  const [grouped, setGrouped] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | closed
  const [categoryFilter, setCategoryFilter] = useState("All");

  const load = () => api.get("/events").then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);

  const loadReservations = async (event) => {
    setViewingRes(event);
    const { data } = await api.get(`/events/${event.event_id}/reservations`);
    setReservations(data);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.event_id) {
        await api.put(`/events/${editing.event_id}`, editing);
        toast.success("Event updated");
      } else {
        await api.post("/events", editing);
        toast.success("Event created");
      }
      setEditing(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const del = async (event) => {
    if (!window.confirm(`Delete "${event.title}"?`)) return;
    try {
      await api.delete(`/events/${event.event_id}`);
      toast.success("Event deleted");
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const cancelAll = async (event) => {
    if (!window.confirm(`Cancel ALL reservations for "${event.title}"?`)) return;
    try {
      const { data } = await api.delete(`/events/${event.event_id}/reservations`);
      toast.success(`${data.cancelled} reservation(s) cancelled`);
      if (viewingRes) loadReservations(event);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const cancelOne = async (reservation) => {
    if (!window.confirm("Cancel this reservation?")) return;
    try {
      await api.delete(`/reservations/${reservation.reservation_id}`);
      toast.success("Reservation cancelled");
      if (viewingRes) loadReservations(viewingRes);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggleStatus = async (event) => {
    const next = event.status === "open" ? "closed" : "open";
    try {
      await api.put(`/events/${event.event_id}`, { ...event, status: next });
      toast.success(`Event ${next === "closed" ? "closed" : "reopened"} for reservations`);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return events
      .filter((e) => categoryFilter === "All" || e.category === categoryFilter)
      .filter((e) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "open") return e.is_open_for_reservation;
        return !e.is_open_for_reservation; // closed: past, manually closed, or full
      })
      .filter((e) =>
        !q ||
        e.title.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
      );
  }, [events, query, categoryFilter, statusFilter]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const byCat = {};
    for (const e of filtered) {
      if (!byCat[e.category]) byCat[e.category] = [];
      byCat[e.category].push(e);
    }
    return CATEGORIES.filter((c) => byCat[c]).map((c) => ({ cat: c, items: byCat[c] }));
  }, [filtered, grouped]);

  const renderItems = (items) =>
    view === "detail" ? (
      <div className="space-y-4">
        {items.map((e) => (
          <EventRow
            key={e.event_id}
            event={e}
            onEdit={() => setEditing({ ...e })}
            onDelete={() => del(e)}
            onToggle={() => toggleStatus(e)}
            onReservations={() => loadReservations(e)}
          />
        ))}
      </div>
    ) : (
      <CompactTable
        items={items}
        onEdit={(e) => setEditing({ ...e })}
        onDelete={del}
        onToggle={toggleStatus}
        onReservations={loadReservations}
      />
    );

  return (
    <div data-testid="events-panel">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="eyebrow mb-2">Events</p>
          <h1 className="font-serif-display text-4xl" style={{ color: "var(--ser-primary-dark)" }}>
            Manage events & reservations
          </h1>
        </div>
        <button onClick={() => setEditing({ ...EMPTY_EVENT })} className="btn-ser-primary"
          data-testid="new-event-btn">
          <Plus className="w-4 h-4" /> New event
        </button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row gap-3 mb-5" data-testid="admin-events-toolbar">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ser-text-muted)" }} />
          <input
            type="search"
            placeholder="Search events by title, description, location…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="ser-input"
            style={{ paddingLeft: "2.75rem" }}
            data-testid="admin-events-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="ser-input lg:w-48"
          data-testid="admin-status-filter"
        >
          <option value="all">All statuses</option>
          <option value="open">Open only</option>
          <option value="closed">Closed only</option>
        </select>
        <button
          onClick={() => setGrouped(!grouped)}
          className="btn-ser-ghost"
          style={{ backgroundColor: grouped ? "var(--ser-sand)" : "transparent" }}
          data-testid="admin-toggle-group"
        >
          <Layers className="w-4 h-4" /> {grouped ? "Ungroup" : "Group by type"}
        </button>
        <div className="flex rounded-full overflow-hidden" style={{ backgroundColor: "var(--ser-sand)" }}>
          <button
            onClick={() => setView("detail")}
            className="px-4 py-2 flex items-center gap-1.5 text-sm"
            style={{
              backgroundColor: view === "detail" ? "var(--ser-primary)" : "transparent",
              color: view === "detail" ? "white" : "var(--ser-text-muted)",
            }}
            data-testid="admin-view-detail-btn"
          >
            <LayoutGrid className="w-4 h-4" /> Cards
          </button>
          <button
            onClick={() => setView("compact")}
            className="px-4 py-2 flex items-center gap-1.5 text-sm"
            style={{
              backgroundColor: view === "compact" ? "var(--ser-primary)" : "transparent",
              color: view === "compact" ? "white" : "var(--ser-text-muted)",
            }}
            data-testid="admin-view-compact-btn"
          >
            <List className="w-4 h-4" /> List
          </button>
        </div>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-category-tabs">
        {["All", ...CATEGORIES].map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            className="px-4 py-1.5 rounded-full text-xs font-semibold transition-all"
            style={{
              backgroundColor: categoryFilter === c ? "var(--ser-primary)" : "var(--ser-sand)",
              color: categoryFilter === c ? "white" : "var(--ser-text-muted)",
            }}
            data-testid={`admin-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {c}
          </button>
        ))}
      </div>

      <p className="text-xs mb-4" style={{ color: "var(--ser-text-muted)" }} data-testid="admin-events-count">
        Showing {filtered.length} of {events.length} events
      </p>

      {events.length === 0 ? (
        <p style={{ color: "var(--ser-text-muted)" }}>No events yet.</p>
      ) : filtered.length === 0 ? (
        <p className="py-8 text-center" style={{ color: "var(--ser-text-muted)" }}>No events match your filters.</p>
      ) : grouped ? (
        <div className="space-y-10">
          {groups.map((g) => (
            <div key={g.cat} data-testid={`admin-group-${g.cat.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="flex items-center gap-3 mb-4">
                <h2 className="font-serif-display text-2xl" style={{ color: "var(--ser-primary-dark)" }}>
                  {g.cat}
                </h2>
                <span className="ser-tag">{g.items.length}</span>
              </div>
              {renderItems(g.items)}
            </div>
          ))}
        </div>
      ) : (
        renderItems(filtered)
      )}

      {/* Editor modal */}
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.event_id ? "Edit event" : "Create event"}>
          <form onSubmit={save} className="space-y-4" data-testid="event-form">
            <div>
              <label className="ser-label">Title</label>
              <input required className="ser-input" value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="event-title-input" />
            </div>
            <div>
              <label className="ser-label">Description</label>
              <textarea required rows={3} className="ser-input" value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="event-desc-input" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="ser-label">Date</label>
                <input required type="date" className="ser-input" value={editing.date?.slice(0, 10) || ""}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid="event-date-input" />
              </div>
              <div>
                <label className="ser-label">Location</label>
                <input required className="ser-input" value={editing.location}
                  onChange={(e) => setEditing({ ...editing, location: e.target.value })} data-testid="event-location-input" />
              </div>
              <div>
                <label className="ser-label">Category</label>
                <select className="ser-input" value={editing.category}
                  onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="event-category-input">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="ser-label">Capacity</label>
                <input type="number" min={1} className="ser-input" value={editing.capacity}
                  onChange={(e) => setEditing({ ...editing, capacity: parseInt(e.target.value, 10) || 1 })}
                  data-testid="event-capacity-input" />
              </div>
              <div>
                <label className="ser-label">Status</label>
                <select className="ser-input" value={editing.status || "open"}
                  onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                  data-testid="event-status-input">
                  <option value="open">Open for reservations</option>
                  <option value="closed">Closed for reservations</option>
                </select>
              </div>
            </div>
            <div>
              <ImageField
                label="Event image"
                value={editing.image_url}
                onChange={(v) => setEditing({ ...editing, image_url: v })}
                testIdPrefix="event-image"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="btn-ser-primary flex-1" data-testid="save-event-btn">
                {editing.event_id ? "Save changes" : "Create event"}
              </button>
              <button type="button" onClick={() => setEditing(null)} className="btn-ser-outline">Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Reservations modal */}
      {viewingRes && (
        <Modal onClose={() => setViewingRes(null)} title={`Reservations · ${viewingRes.title}`} wide>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: "var(--ser-text-muted)" }}>
              {reservations.filter((r) => r.status === "active").length} active · {reservations.length} total
            </p>
            {reservations.some((r) => r.status === "active") && (
              <button onClick={() => cancelAll(viewingRes)} className="btn-ser-outline text-sm"
                style={{ borderColor: "var(--ser-clay)", color: "var(--ser-clay-dark)" }}
                data-testid="cancel-all-res-btn">
                <XCircle className="w-4 h-4" /> Cancel all active
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--ser-text-muted)" }}>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Name</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Email</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Cell</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Dietary</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Comments</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold">Status</th>
                  <th className="py-2 pr-3 text-xs uppercase tracking-wider font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {reservations.length === 0 && (
                  <tr><td colSpan={7} className="py-6 text-center" style={{ color: "var(--ser-text-muted)" }}>No reservations yet.</td></tr>
                )}
                {reservations.map((r) => (
                  <tr key={r.reservation_id} className="border-t" style={{ borderColor: "var(--ser-sand-dark)" }}
                    data-testid={`admin-res-${r.reservation_id}`}>
                    <td className="py-3 pr-3">{r.name}</td>
                    <td className="py-3 pr-3">{r.email}</td>
                    <td className="py-3 pr-3">{r.cell_number}</td>
                    <td className="py-3 pr-3">
                      <span className="ser-tag" style={{ backgroundColor: "var(--ser-sand)", color: "var(--ser-primary-dark)" }}>
                        {r.dietary_requirements || "None"}
                      </span>
                    </td>
                    <td className="py-3 pr-3 max-w-xs">
                      <span className="block text-xs line-clamp-2" style={{ color: "var(--ser-text-muted)" }}>
                        {r.comments || "—"}
                      </span>
                    </td>
                    <td className="py-3 pr-3">
                      <span className={r.status === "active" ? "ser-tag" : "ser-tag"}
                        style={r.status !== "active" ? { backgroundColor: "var(--ser-sand)", color: "var(--ser-text-muted)" } : {}}>
                        {r.status}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      {r.status === "active" && (
                        <button onClick={() => cancelOne(r)} className="btn-ser-ghost text-xs"
                          style={{ color: "var(--ser-clay-dark)" }}
                          data-testid={`cancel-res-${r.reservation_id}`}>
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ─── Event row helpers (admin) ─── */
function StatusPillAdmin({ event }) {
  const label = event.is_past
    ? "Closed · Past"
    : event.status === "closed"
    ? "Closed"
    : event.reservations_count >= event.capacity
    ? "Closed · Full"
    : "Open";
  const isOpen = event.is_open_for_reservation;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase"
      style={{
        backgroundColor: isOpen ? "rgba(39,68,40,0.12)" : "rgba(201,122,62,0.14)",
        color: isOpen ? "var(--ser-primary)" : "var(--ser-clay-dark)",
      }}
      data-testid={`admin-status-${event.event_id}`}
    >
      {label}
    </span>
  );
}

function EventRow({ event: e, onEdit, onDelete, onToggle, onReservations }) {
  return (
    <div className="ser-card p-5" data-testid={`admin-event-${e.event_id}`}>
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-40 h-24 rounded-lg overflow-hidden shrink-0">
          <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="font-serif-display text-xl" style={{ color: "var(--ser-primary-dark)" }}>{e.title}</h3>
            <span className="ser-tag">{e.category}</span>
            <StatusPillAdmin event={e} />
          </div>
          <p className="text-sm line-clamp-2 mb-2" style={{ color: "var(--ser-text-muted)" }}>{e.description}</p>
          <div className="text-xs flex flex-wrap gap-3" style={{ color: "var(--ser-text-muted)" }}>
            <span>{formatDate(e.date)}</span>
            <span>·</span>
            <span>{e.location}</span>
            <span>·</span>
            <span>{e.reservations_count}/{e.capacity} reserved</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-start">
          {!e.is_past && (
            <button onClick={onToggle} className="btn-ser-ghost text-sm" data-testid={`toggle-status-${e.event_id}`}>
              {e.status === "open" ? "Close" : "Reopen"}
            </button>
          )}
          <button onClick={onReservations} className="btn-ser-ghost text-sm" data-testid={`view-res-${e.event_id}`}>
            <Users className="w-4 h-4" /> Reservations
          </button>
          <button onClick={onEdit} className="btn-ser-ghost text-sm" data-testid={`edit-event-${e.event_id}`}>
            <Pencil className="w-4 h-4" /> Edit
          </button>
          <button onClick={onDelete} className="btn-ser-ghost text-sm" style={{ color: "var(--ser-clay-dark)" }}
            data-testid={`delete-event-${e.event_id}`}>
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>
      {e.reservations_count > 0 && (
        <div className="mt-3 p-3 rounded-lg flex items-start gap-2 text-xs"
          style={{ backgroundColor: "rgba(201,122,62,0.08)", color: "var(--ser-clay-dark)" }}>
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          This event has active reservations — you must cancel them before it can be deleted.
        </div>
      )}
    </div>
  );
}

function CompactTable({ items, onEdit, onDelete, onToggle, onReservations }) {
  return (
    <div className="ser-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-events-compact-table">
          <thead style={{ backgroundColor: "var(--ser-sand)" }}>
            <tr className="text-left" style={{ color: "var(--ser-primary-dark)" }}>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Title</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Category</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Date</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Location</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Reserved</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold">Status</th>
              <th className="p-3 text-xs uppercase tracking-wider font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.event_id} className="border-t" style={{ borderColor: "var(--ser-sand-dark)" }}
                data-testid={`admin-event-row-${e.event_id}`}>
                <td className="p-3 font-medium" style={{ color: "var(--ser-primary-dark)" }}>{e.title}</td>
                <td className="p-3"><span className="ser-tag">{e.category}</span></td>
                <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{formatDate(e.date)}</td>
                <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{e.location}</td>
                <td className="p-3 text-xs" style={{ color: "var(--ser-text-muted)" }}>{e.reservations_count}/{e.capacity}</td>
                <td className="p-3"><StatusPillAdmin event={e} /></td>
                <td className="p-3">
                  <div className="flex gap-1 justify-end">
                    {!e.is_past && (
                      <button onClick={() => onToggle(e)} className="btn-ser-ghost text-xs px-2"
                        data-testid={`toggle-status-${e.event_id}`}>
                        {e.status === "open" ? "Close" : "Reopen"}
                      </button>
                    )}
                    <button onClick={() => onReservations(e)} className="btn-ser-ghost text-xs px-2"
                      title="Reservations" data-testid={`view-res-${e.event_id}`}>
                      <Users className="w-4 h-4" />
                    </button>
                    <button onClick={() => onEdit(e)} className="btn-ser-ghost text-xs px-2"
                      title="Edit" data-testid={`edit-event-${e.event_id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => onDelete(e)} className="btn-ser-ghost text-xs px-2"
                      style={{ color: "var(--ser-clay-dark)" }} title="Delete"
                      data-testid={`delete-event-${e.event_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}



